// src/helpers/pdfOrdenPagoRemito.js

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getOrdenPagoRemitoById } from "./rutaOrdenPagoRemitos";
import {
  fmtDate,
  fmtNum,
  S,
  loadImageAsDataURL,
} from "./pdfCommonSanPancracio";

const $ = (n) => fmtNum(n);

const DEFAULT_LOGO_URL = "/SanPancracio.png";

const DEFAULT_EMPRESA = {
  nombre: "SAN PANCRACIO SRL",
  linea1:
    "Avda. Peron 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán",
  linea2:
    "Tel: 0381-3008829 • contacto@sanpancracio.com",

  // Datos institucionales para el nuevo formato
  cuit: "33-71090617-9",
  iibb: "33-71090617-9",
  inicioActividad: "01/07/2009",
};

const sumImporte = (arr = []) =>
  arr.reduce((acc, x) => acc + Number(x?.importe || 0), 0);

const isCheque = (medio) =>
  String(medio || "").toLowerCase() === "cheque";

const isICheq = (medio) =>
  String(medio || "").toLowerCase() === "icheq";

const pickCuit = (entidad) =>
  S(
    entidad?.cuit ||
      entidad?.CUIT ||
      entidad?.cuitCuil ||
      entidad?.cuit_cuil ||
      ""
  );

const pickDireccion = (entidad) => {
  const d =
    entidad?.direccion ||
    entidad?.domicilio ||
    entidad?.direccionFiscal ||
    entidad?.direccionCompleta ||
    "";

  if (!d) return "";

  if (typeof d === "string") {
    return S(d);
  }

  if (typeof d === "object") {
    return [
      d.calle,
      d.numero,
      d.piso ? `Piso ${d.piso}` : "",
      d.dpto ? `Dpto ${d.dpto}` : "",
      d.localidad,
      d.provincia,
      d.cp ? `CP ${d.cp}` : "",
    ]
      .filter(Boolean)
      .join(" - ");
  }

  return "";
};

const pickBanco = (p) =>
  S(
    p?.bancoEmisor ||
      p?.banco ||
      p?.carteraId?.bancoEmisor ||
      p?.carteraId?.banco ||
      p?.cartera?.bancoEmisor ||
      p?.cartera?.banco ||
      p?.cuentaBancaria?.banco ||
      p?.cuentaBancaria?.alias ||
      ""
  );

const pickNro = (p) =>
  S(
    p?.nroCheque ||
      p?.nro ||
      p?.idICheq ||
      p?.referencia ||
      p?.carteraId?.nro ||
      p?.carteraId?.nroCheque ||
      p?.carteraId?.idICheq ||
      ""
  );

const pickFechaValor = (p) =>
  fmtDate(
    p?.fechaCheque ||
      p?.fechaValor ||
      p?.carteraId?.fechaCheque ||
      p?.carteraId?.fechaPago ||
      p?.cartera?.fechaCheque ||
      p?.cartera?.fechaPago ||
      p?.fecha ||
      null
  );

const tipoValor = (p) => {
  const medio = String(p?.medio || "").toLowerCase();

  if (medio === "icheq") return "ECHEQ";
  if (medio === "cheque") return "CPD";

  return String(p?.medio || "").toUpperCase();
};

function calcTotalesMedios(pagos = []) {
  const totales = {
    efectivo: 0,
    transferencia: 0,
    cheque: 0,
    icheq: 0,
    otro: 0,
    total: 0,
  };

  for (const p of pagos) {
    const medio = String(p?.medio || "").toLowerCase();
    const importe = Number(p?.importe || 0);

    if (!importe) continue;

    if (medio === "efectivo") {
      totales.efectivo += importe;
    } else if (medio === "transferencia") {
      totales.transferencia += importe;
    } else if (medio === "cheque") {
      totales.cheque += importe;
    } else if (medio === "icheq") {
      totales.icheq += importe;
    } else {
      totales.otro += importe;
    }

    totales.total += importe;
  }

  Object.keys(totales).forEach((k) => {
    totales[k] = Number(totales[k].toFixed(2));
  });

  return totales;
}

function numeroOP(op) {
  const nuevos = [op?.numeroA, op?.numeroB].filter(Boolean);

  if (nuevos.length) {
    return nuevos.join(" / ");
  }

  return op?.numero || "-";
}

function nombreArchivoOP(op) {
  const nuevos = [op?.numeroA, op?.numeroB].filter(Boolean);

  if (nuevos.length) {
    return nuevos.join("_");
  }

  return op?.numero || "OP";
}

function dibujarCabeceraEmpresa(doc, {
  logoDataURL,
  empresa,
  op,
}) {
  const x = 12;
  const y = 10;
  const w = 142;
  const h = 50;

  doc.setDrawColor(45, 45, 45);
  doc.setLineWidth(0.35);

  doc.roundedRect(x, y, w, h, 8, 8);

  // Logo
  if (logoDataURL) {
    try {
      doc.addImage(
        logoDataURL,
        "PNG",
        x + 5,
        y + 8,
        37,
        28,
        undefined,
        "FAST"
      );
    } catch (e) {
      console.error("Error insertando logo OP:", e);
    }
  }

  const tx = x + 48;

  doc.setTextColor(20, 20, 20);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.text(
    S(empresa.nombre || "SAN PANCRACIO SRL"),
    tx,
    y + 9
  );

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.6);

  const ancho = w - 52;

  let yy = y + 16;

  if (empresa.linea1) {
    const lineas = doc.splitTextToSize(
      S(empresa.linea1),
      ancho
    );

    doc.text(lineas, tx, yy);
    yy += lineas.length * 3.2;
  }

  if (empresa.linea2) {
    const lineas = doc.splitTextToSize(
      S(empresa.linea2),
      ancho
    );

    doc.text(lineas, tx, yy);
    yy += lineas.length * 3.2;
  }

  yy += 1.5;

  if (empresa.cuit) {
    doc.text(`CUIT: ${empresa.cuit}`, tx, yy);
    yy += 4;
  }

  if (empresa.iibb) {
    doc.text(`ING. BRUTOS: ${empresa.iibb}`, tx, yy);
    yy += 4;
  }

  if (empresa.inicioActividad) {
    doc.text(
      `INICIO DE ACTIVIDAD: ${empresa.inicioActividad}`,
      tx,
      yy
    );
  }

  // Lado derecho: título y numeración
  const rx = 161;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("ORDEN DE PAGO", rx, y + 14);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);

  let ny = y + 25;

  if (op?.numeroA) {
    doc.text(`OP A N°: ${op.numeroA}`, rx, ny);
    ny += 5;
  }

  if (op?.numeroB) {
    doc.text(`OP B N°: ${op.numeroB}`, rx, ny);
    ny += 5;
  }

  if (!op?.numeroA && !op?.numeroB) {
    doc.text(`OP N°: ${op?.numero || "-"}`, rx, ny);
    ny += 5;
  }

  doc.text(
    `Fecha: ${fmtDate(op?.fecha)}`,
    rx,
    ny + 1
  );
}

function dibujarProveedor(doc, op, y) {
  const x = 12;
  const w = 186;
  const h = 34;

  doc.setDrawColor(45, 45, 45);
  doc.setLineWidth(0.35);
  doc.roundedRect(x, y, w, h, 4, 4);

  // Título sobre borde
  doc.setFillColor(255, 255, 255);
  doc.rect(x + 9, y - 2, 24, 5, "F");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text("Proveedor", x + 12, y + 1.5);

  const proveedor = op?.proveedor || {};

  const nombre = S(
    proveedor?.razonsocial ||
      proveedor?.nombre ||
      "-"
  );

  const direccion = pickDireccion(proveedor);
  const cuit = pickCuit(proveedor);

  let yy = y + 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.text(nombre, x + 8, yy);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);

  yy += 5;

  if (direccion) {
    const lineas = doc.splitTextToSize(
      `Dirección: ${direccion}`,
      168
    );

    doc.text(lineas, x + 8, yy);
    yy += lineas.length * 4;
  }

  if (cuit) {
    doc.text(`CUIT: ${cuit}`, x + 8, yy);
  }

  return y + h;
}

function dibujarResumenMedios(
  doc,
  {
    pagos,
    retenciones,
    y,
  }
) {
  const totales = calcTotalesMedios(pagos);

  const totalRetenciones = sumImporte(retenciones);

  const soloGanancias =
    retenciones.length > 0 &&
    retenciones.every(
      (r) =>
        ["GAN", "GANANCIAS"].includes(
          String(r?.tipo || "").toUpperCase()
        )
    );

  const tituloRet =
    soloGanancias
      ? "Ret. Ganancias"
      : "Retenciones";

  autoTable(doc, {
    startY: y,
    margin: {
      left: 12,
      right: 12,
    },

    tableWidth: 186,

    theme: "grid",

    head: [[
      tituloRet,
      "Efectivo",
      "CPD",
      "Echeq",
      "Transferencia",
    ]],

    body: [[
      totalRetenciones
        ? `${$(totalRetenciones)}`
        : "",
      totales.efectivo
        ? `${$(totales.efectivo)}`
        : "",
      totales.cheque
        ? `${$(totales.cheque)}`
        : "",
      totales.icheq
        ? `${$(totales.icheq)}`
        : "",
      totales.transferencia
        ? `${$(totales.transferencia)}`
        : "",
    ]],

    styles: {
      font: "helvetica",
      fontSize: 7.5,
      textColor: [20, 20, 20],
      lineColor: [80, 80, 80],
      lineWidth: 0.25,
      cellPadding: 1.4,
      halign: "center",
      valign: "middle",
    },

    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [20, 20, 20],
      fontStyle: "normal",
      halign: "center",
    },

    bodyStyles: {
      fillColor: [235, 235, 235],
      textColor: [20, 20, 20],
      halign: "center",
    },

    columnStyles: {
      0: { cellWidth: 37.2 },
      1: { cellWidth: 37.2 },
      2: { cellWidth: 37.2 },
      3: { cellWidth: 37.2 },
      4: { cellWidth: 37.2 },
    },
  });

  const finalY = doc.lastAutoTable.finalY;

  return {
    y: finalY,
    totales,
    totalRetenciones,
  };
}

function dibujarTotal(
  doc,
  y,
  total
) {
  const x = 12;
  const w = 186;
  const h = 8;

  doc.setDrawColor(70, 70, 70);
  doc.setLineWidth(0.35);
  doc.roundedRect(x, y, w, h, 1.5, 1.5);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);

  doc.text(
    "TOTAL :",
    150,
    y + 5.3,
    { align: "right" }
  );

  doc.setFont("helvetica", "normal");

  doc.text(
    "$",
    158,
    y + 5.3
  );

  doc.text(
    $(total),
    194,
    y + 5.3,
    { align: "right" }
  );

  return y + h;
}

function dibujarDetalleValores(
  doc,
  pagos,
  y
) {
  const valores = (pagos || []).filter(
    (p) =>
      isCheque(p?.medio) ||
      isICheq(p?.medio)
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.text("Detalle Valores", 12, y);

  y += 4;

  autoTable(doc, {
    startY: y,

    margin: {
      left: 12,
      right: 12,
    },

    tableWidth: 186,

    theme: "plain",

    head: [[
      "Fecha",
      "Nro",
      "Banco",
      "Importe",
      "Tipo",
    ]],

    body: valores.map((p) => [
      pickFechaValor(p),
      pickNro(p),
      pickBanco(p),
      `$ ${$(p?.importe)}`,
      tipoValor(p),
    ]),

    styles: {
      font: "helvetica",
      fontSize: 7.2,
      textColor: [30, 30, 30],
      cellPadding: 1.1,
      overflow: "ellipsize",
    },

    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [20, 20, 20],
      fontStyle: "normal",
      halign: "center",

      lineColor: [60, 60, 60],
      lineWidth: {
        top: 0.3,
        bottom: 0.3,
        left: 0.3,
        right: 0.3,
      },
    },

    alternateRowStyles: {
      fillColor: [232, 232, 232],
    },

    columnStyles: {
      0: {
        halign: "center",
        cellWidth: 31,
      },

      1: {
        halign: "center",
        cellWidth: 34,
      },

      2: {
        halign: "center",
        cellWidth: 42,
      },

      3: {
        halign: "right",
        cellWidth: 48,
      },

      4: {
        halign: "center",
        cellWidth: 31,
      },
    },

    didParseCell: (data) => {
      if (data.section === "body") {
        data.cell.styles.fillColor =
          data.row.index % 2 === 0
            ? [232, 232, 232]
            : [255, 255, 255];
      }
    },
  });

  let finalY = doc.lastAutoTable.finalY;

  // Rayado visual vacío como el modelo original.
  // Sólo rellena si queda lugar.
  const limite = 239;
  const altoFila = 5.5;

  let fila = valores.length;

  while (finalY + altoFila < limite) {
    if (fila % 2 === 0) {
      doc.setFillColor(232, 232, 232);

      doc.rect(
        12,
        finalY,
        186,
        altoFila,
        "F"
      );
    }

    finalY += altoFila;
    fila += 1;
  }

  return finalY;
}

function dibujarFirmas(doc) {
  const x = 137;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);

  doc.text("Recepción", x, 263);
  doc.text("Firma", x, 274);
  doc.text("Aclaración", x, 285);
}

/**
 * PDF Orden de Pago - formato solicitado por Inés.
 *
 * Replica visualmente la página principal del modelo:
 * - logo + datos empresa
 * - título y numeración
 * - proveedor
 * - resumen medios / retenciones
 * - total
 * - detalle de valores
 * - recepción / firma / aclaración
 */
export async function generarPDFOrdenPagoRemito(
  id,
  opts = {}
) {
  const r = await getOrdenPagoRemitoById(id);
  const op = r?.orden;

  if (!op) {
    alert("No se encontró la orden.");
    return;
  }

  const logoUrl =
    opts.logoUrl ||
    DEFAULT_LOGO_URL;

  const empresa = {
    ...DEFAULT_EMPRESA,
    ...(opts.empresa || {}),
  };

  let logoDataURL = null;

  try {
    if (logoUrl) {
      logoDataURL =
        await loadImageAsDataURL(logoUrl);
    }
  } catch (e) {
    console.error(
      "Error cargando logo OP:",
      e
    );
  }

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // CABECERA
  dibujarCabeceraEmpresa(doc, {
    logoDataURL,
    empresa,
    op,
  });

  // PROVEEDOR
  let y = 68;

  y = dibujarProveedor(
    doc,
    op,
    y
  );

  // RESUMEN MEDIOS
  y += 8;

  const pagos =
    Array.isArray(op?.pagos)
      ? op.pagos
      : [];

  const retenciones =
    Array.isArray(op?.retenciones)
      ? op.retenciones
      : [];

  const resumen = dibujarResumenMedios(
    doc,
    {
      pagos,
      retenciones,
      y,
    }
  );

  y = resumen.y + 7;

  /*
   * IMPORTANTE:
   * La OP considera las retenciones como parte del pago
   * aplicado al proveedor.
   *
   * TOTAL = medios efectivamente entregados
   *       + retenciones practicadas
   */
  const totalGeneral = Number(
    (
      resumen.totales.total +
      resumen.totalRetenciones
    ).toFixed(2)
  );

  y = dibujarTotal(
    doc,
    y,
    totalGeneral
  );

  // DETALLE DE CHEQUES / ICHEQ
  y += 8;

  dibujarDetalleValores(
    doc,
    pagos,
    y
  );

  // FIRMAS
  dibujarFirmas(doc);

  const nro = numeroOP(op);

  // Metadata PDF
  try {
    doc.setProperties({
      title: `Orden de Pago ${nro}`,
      subject:
        "Orden de Pago - San Pancracio",
      author:
        empresa.nombre ||
        "San Pancracio",
      creator:
        "Sistema San Pancracio",
    });
  } catch (e) {
    // No bloquea la generación
  }

  doc.save(
    `${nombreArchivoOP(op)}.pdf`
  );
}